import classes from "./profile.module.css";
import Abilities from "../../../components/Onboarding/Review/Abilities/abilities";
import { IconCheck, IconFileCheck, IconX } from "@tabler/icons-react";
import { useNavigate } from "react-router";
import { UserContext } from "../../../context";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  findTalentSubscription,
  getTalentImprovementAnswers,
  getTalentPaymentHistory,
  getTalentSubscription,
  talentProfile,
  talentSubscribe,
} from "../../../services/talentCalls";
import { errorHandler } from "../../../utils";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";
import {
  AiInsightsContents,
  AiInsightsVisual,
} from "../../../components/Onboarding/Review/AiInsights";
import { preloadImagesWithCORS } from "../../../utils/functions";
import { toJpeg } from "html-to-image";
import {
  DEFAULT_TALENT_SUBSCRIPTION,
  TRANSPARENT_PIXEL,
} from "../../../constants";
import LearningPath from "../../../components/LearningPath/learningPath";
import Improvement from "../../../components/LearningPath/improvement";
import TalentProfileHeader from "../../../components/Onboarding/Review/Profile/talent-profile";
import testStyles from "../Test/testRedesign.module.css";
import Reveal from "../../../components/Reveal/Reveal";
import ProfileStrengthCard from "../../../components/Onboarding/Review/components/ProfileStrengthCard";
import TabBar from "../../../components/Onboarding/Review/components/TabBar";
import OverviewTab from "../../../components/Onboarding/Review/components/OverviewTab";
import ExperienceTab from "../../../components/Onboarding/Review/components/ExperienceTab";
import InsightsTab from "../../../components/Onboarding/Review/components/InsightsTab";
import CareerPlanTab from "../../../components/Onboarding/Review/components/CareerPlanTab";
import MediaTab from "../../../components/Onboarding/Review/components/MediaTab";
import DisclaimerBar from "../../../components/Onboarding/Review/components/DisclaimerBar";
import ShareModal from "../../../components/Onboarding/Review/components/ShareModal";
import styles from "./profile.module.css";

export const disclaimerText =
  "I understand that my Awasource AI Insights are based on the information I provide and automated analysis. They are for informational and career support purposes only and do not guarantee any specific employment outcome. I will use my own judgment when making career decisions.";

const ProfileReview = ({ type }) => {
  let navigate = useNavigate();
  const { data: profileData, isDev, setSkillModal } = useContext(UserContext);
  const [load, setLoad] = useState(false);
  const [data, setData] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [activeSubscripion, setActiveSubscripion] = useState(null);
  const [disclaimerModal, setDisclaimerModal] = useState(false);
  const [disclaimerAgreed, setDisclaimerAgreed] = useState(false);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [improvementTestResult, setImprovementTestResult] = useState([]);
  const [selectedPlanName, setSelectedPlanName] = useState(
    DEFAULT_TALENT_SUBSCRIPTION,
  ); // TODO: Update when new plans are added with cards
  const targetRef = useRef();

  const getUserProfile = async () => {
    try {
      setLoad(true);
      const res = await talentProfile();
      if (res?.data.data.talent) {
        setData(res.data.data.talent);
      }
      setLoad(false);
    } catch (error) {
      setLoad(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    // Check if profileData is empty
    if (profileData && Object.keys(profileData)?.length > 0) {
      setData(profileData);
    } else {
      getUserProfile();
    }
  }, [profileData]);

  const handleDownloadProfile = () => {
    window.open("/talent-profile/report", "_blank", "noopener,noreferrer");
  };

  // Wait for all images in a container
  const waitForImages = (element) => {
    const images = Array.from(element.querySelectorAll("img"));

    return Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve();

        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = () => {
            resolve();
          };
        });
      }),
    );
  };

  const downloadImage = async () => {
    if (!targetRef.current) return;

    targetRef.current.classList.add("exporting");

    try {
      setIsExporting(true);
      // wait for all images to finish loading or fail
      await preloadImagesWithCORS(targetRef.current);
      await waitForImages(targetRef.current);

      const dataUrl = await toJpeg(targetRef.current, {
        pixelRatio: 2,
        skipFonts: true,
        backgroundColor: "#fff",
        imagePlaceholder: TRANSPARENT_PIXEL, // fallback
        cacheBust: true,
      });

      if (dataUrl) {
        const link = document.createElement("a");
        link.download = "profile.jpg";
        link.href = dataUrl;
        link.click();
      }
    } catch (err) {
    } finally {
      setIsExporting(false);
      targetRef.current.classList.remove("exporting");
    }
  };

  const handleGetSubscriptions = async () => {
    setSubscribeLoading(true);
    try {
      const res = await getTalentSubscription();
      if (res?.data?.data && Array.isArray(res?.data?.data?.records)) {
        const hasActiveSubscription = res?.data?.data?.records?.some(
          (sub) =>
            sub?.isActive && sub?.paymentStatus?.toLowerCase() === "completed",
        );
        setActiveSubscripion(hasActiveSubscription);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleFindTalentSubscription = async () => {
    try {
      const payload = {
        name: selectedPlanName,
      };
      const res = await findTalentSubscription(payload);
      const subscriptionId = res?.data?.data?.subscription?._id;
      if (subscriptionId) {
        return subscriptionId;
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const handleTalentSubscribe = useCallback(async () => {
    setSubscribeLoading(true);
    try {
      const subscriptionId = await handleFindTalentSubscription();
      if (subscriptionId) {
        const res = await talentSubscribe(subscriptionId);
        if (res?.data?.data?.data?.data?.authorization_url) {
          window.location.href = res?.data?.data?.data?.data?.authorization_url;
        }
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSubscribeLoading(false);
    }
  }, [selectedPlanName]);

  const handleTalentPaymentHistory = async () => {
    setSubscribeLoading(true);
    try {
      const res = await getTalentPaymentHistory();
      if (res.data.data.data.data.authorization_url) {
        window.location.href = res?.data?.data?.data?.data?.authorization_url;
      }
    } catch (error) {
      console.error("");
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleGetImprovementAnswers = async () => {
    setAnswersLoading(true);
    try {
      const res = await getTalentImprovementAnswers();
      if (res?.data?.data?.results) {
        setImprovementTestResult(res?.data?.data?.results);
      }
    } catch (error) {
      console.error("");
    } finally {
      setAnswersLoading(false);
    }
  };

  useEffect(() => {
    handleGetSubscriptions();
    handleGetImprovementAnswers();
  }, []);

  useEffect(() => {
    if (data?.hasActiveSubscription) {
      setActiveSubscripion(true);
    }
  }, [data]);

  const [activeTab, setActiveTab] = useState("overview");
  const [revealTicks, setRevealTicks] = useState({
    overview: 1,
    experience: 0,
    insights: 0,
    career: 0,
    media: 0,
  });
  const [shareOpen, setShareOpen] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);

  function handleTabChange(id) {
    setActiveTab(id);
    setRevealTicks((prev) => ({ ...prev, [id]: prev[id] + 1 }));
  }

  return (
    <div className={styles.main}>
      {(load || answersLoading || subscribeLoading || isExporting) && (
        <FullScreenLoader />
      )}

      <Reveal index={0} baseDelay={40} step={70} resetKey="mount">
        <TalentProfileHeader
          data={data}
          ringPercent={0}
          onShare={() => setShareOpen(true)}
        />
      </Reveal>

      <Reveal index={1} baseDelay={40} step={70} resetKey="mount">
        <ProfileStrengthCard data={data} />
      </Reveal>

      <Reveal index={2} baseDelay={40} step={70} resetKey="mount">
        <TabBar activeTab={activeTab} onChange={handleTabChange} />
      </Reveal>

      <div className={activeTab === "overview" ? "" : styles.hiddenPanel}>
        <OverviewTab data={data} resetKey={revealTicks.overview} />
      </div>

      <div className={activeTab === "experience" ? "" : styles.hiddenPanel}>
        <ExperienceTab data={data} resetKey={revealTicks.experience} />
      </div>

      <div className={activeTab === "insights" ? "" : styles.hiddenPanel}>
        <InsightsTab data={data} resetKey={revealTicks.insights} />
      </div>

      <div className={activeTab === "career" ? "" : styles.hiddenPanel}>
        <CareerPlanTab
          data={improvementTestResult}
          isUnlock={activeSubscripion}
          setDisclaimerModal={setDisclaimerModal}
        />
      </div>

      <div className={activeTab === "media" ? "" : styles.hiddenPanel}>
        <MediaTab data={data} resetKey={revealTicks.media} />
      </div>

      <DisclaimerBar
        text={disclaimerText}
        checked={disclaimerChecked}
        onCheckedChange={setDisclaimerChecked}
        onDownload={handleDownloadProfile}
      />

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        name={`${data?.firstName} ${data?.lastName}`}
        headline={data?.pryRole}
      />


      {disclaimerModal && (
        <div className={testStyles.modalOverlay}>
          <div className={testStyles.modal}>
            <button
              className={testStyles.modalCloseBtn}
              onClick={() => setDisclaimerModal(false)}
              aria-label="Close"
            >
              <IconX size={15} />
            </button>
            <div className={testStyles.modalContent}>
              <div className={testStyles.modalIllustrationWrap}>
                <IconFileCheck size={48} color="#fff" />
              </div>
              <label className={testStyles.disclaimerRow}>
                <input
                  type="checkbox"
                  className={testStyles.checkboxInput}
                  checked={disclaimerAgreed}
                  onChange={(e) => setDisclaimerAgreed(e.target.checked)}
                />
                <span className={testStyles.checkboxBox}>{disclaimerAgreed && <IconCheck size={15} />}</span>
                <span className={testStyles.disclaimerText}>
                  I acknowledge that Awasource will process my profile, CV, and
                  assessment responses, including through automated and AI-assisted
                  systems, to generate personalized AI Insights in accordance with
                  the Privacy Policy and I understand that these insights provide
                  guidance on my strengths, potential career paths, and
                  opportunities to help me make informed decisions. While individual
                  outcomes may vary, the insights are designed to support my
                  professional growth.
                </span>
              </label>
              <button
                className={testStyles.btnAmber}
                disabled={!disclaimerAgreed}
                onClick={() => {
                  setDisclaimerModal(false);
                  setDisclaimerAgreed(false);
                  setSkillModal(true);
                }}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileReview;
